import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { fromJS } from 'immutable';
import validator from 'validator';
import { uniqueId } from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import * as _ from 'lodash';
import injectReducer from 'utils/injectReducer';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import { makeSelectOptimumMedicalBenefitsTugo, makeSelectOptimumMedicalBenefitsTugoErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';
import { MEDICAL_EXPENSE_TYPES, PROFESSIONAL_SERVICES_PROVIDERS } from './constants';

import PageSectionHeader from '../components/PageSectionHeader';
import {
  ValidateGroup,
} from '../../../components/Validation';
import {
  handleFormDataChange,
  nextSlide,
  resetFormFields,
} from './actions';
import OptionsContainer from '../components/OptionsContainer';
import { saveFormData } from '../actions';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import {
  ErrorMessage,
  Validate,
} from '../../../components/Validation/';
import { required } from '../../../utils/validation/validator';
import { AdditionalList } from './components/Additional';
import { makeSelectInsuredsList, makeSelectIsClaimForFieldEnabled, makeSelectFormHasChanges } from '../../App/selectors';
import { setFormHasChanges } from '../../App/actions';
import FlexBox from '../../../components/FlexBox';
import { stepOptimumMedicalBenefitsTugoLookup } from '../dataMapper/claimCodeMapper';
import WizardNavigationButtons from '../components/WizardNavigationButtons';
import DatePicker from '../components/DatePicker/index';
import FNOLTooltip from '../../../components/FNOLTooltip';

export class OptimumMedicalBenefitsTugo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
    };

    this.radioFormResets = {
      ambulanceGroundService: {
        true: ['ambulanceAirService', 'infoAirAmbulanceItems', 'infoOtherAmbulanceItems'],
        false: ['infoGroundAmbulanceItems'],
      },
      ambulanceAirService: {
        true: ['infoOtherAmbulanceItems'],
        false: ['infoAirAmbulanceItems'],
      },
    };

    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.saveButtonId = uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
       this.setState((prevState) => ({ validationCounter: prevState.validationCounter + 1 }), () => {
        this.validatorGroup.checkValidation();
      });
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onChange = (propertyName) => (event) => {
    if (event === null) {
      this.props.onFormDataChange(propertyName, null);
      this.props.setFormHasChanges(true);
      return;
    }

    if (event.constructor.name === 'Moment') {
      this.props.onFormDataChange(propertyName, removeTimeZoneFromDate(event.toDate().toString()));
    } else {
      const target = event.target;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      if (target.type === 'radio') {
        this.props.adjust();
      }
      this.props.onFormDataChange(propertyName, value);
    }
    this.props.setFormHasChanges(true);
  };

  onResize = () => {
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  };

  summaryFormValue() {
    return _.mapValues(this.props.form, (prop) => prop || 'N/A');
  }

  handleExpensesSelected = (propertyName) => (event) => {
    const { whatExpensesAreYouClaimingFor } = this.props.form;
    const expensesSelected = !!whatExpensesAreYouClaimingFor && typeof whatExpensesAreYouClaimingFor.toJS === 'function'
      ? whatExpensesAreYouClaimingFor.toJS()
      : whatExpensesAreYouClaimingFor || [];
    const listChecked = _.cloneDeep(expensesSelected);

    const target = event.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (value) {
      listChecked.push(name);
    } else {
      listChecked.splice(listChecked.indexOf(name), 1);
      if (name === "Doctor's Visit") {
        this.props.onResetFormCustom([
          'infoOutpatientPhysicianFeesItems',
        ]);
      }
      if (name === MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS) {
        this.props.onResetFormCustom(['infoDiagnosticTestsItems']);
      }
      if (name === MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS) {
        this.props.onResetFormCustom(['infoPrescriptionDrugsItems']);
      }
      if (name === MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) {
        this.props.onResetFormCustom([
          'ambulanceGroundService',
          'ambulanceAirService',
          'infoGroundAmbulanceItems',
          'infoAirAmbulanceItems',
          'infoOtherAmbulanceItems',
        ]);
      }
      if (name === MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) {
        this.props.onResetFormCustom([
          'hospitalizationHospitalFees',
          'hospitalizationInpatientPhysicianFees',
          'hospitalizationXrayLabsDiagnostics',
          'hospitalizationEmergencyAirTransportation',
          'hospitalizationAirfareReturnHome',
          'hospitalizationFamilyTransportation',
          'hospitalizationFamilyTransportationMealsAccommodation',
          'hospitalizationMedicalAttendant',
          'hospitalizationReturnOfTravellingCompanion',
          'hospitalizationOutOfPocketExpenses',
          'hospitalizationReturnToUSA',
          'hospitalizationEscortOfChildren',
          'hospitalizationReturnOfVehicle',
          'infoHospitalFeesItems',
          'infoInpatientPhysicianFeesItems',
          'infoXrayLabsDiagnosticsItems',
          'infoEmergencyAirTransportationItems',
          'infoAirfareReturnHomeItems',
          'infoFamilyTransportationItems',
          'infoFamilyTransportationMealsAccommodationItems',
          'infoMedicalAttendantItems',
          'infoReturnOfTravellingCompanionItems',
          'infoOutOfPocketExpensesItems',
          'infoReturnToUSAItems',
          'infoEscortOfChildrenItems',
        ]);
      }
      if (name === MEDICAL_EXPENSE_TYPES.RETURN_OF_VEHICLE) {
        this.props.onResetFormCustom([
          'hospitalizationReturnOfVehicle',
          'returnOfVehicleCommercialAgency',
          'returnOfVehicleFamilyMemberFriend',
          'infoReturnOfVehicleItems',
          'infoReturnOfVehicleCommercialAgencyItems',
          'infoReturnOfVehicleFamilyMemberFriendItems',
        ]);
      }
      if (name === MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES) {
        this.props.onResetFormCustom(['infoEssentialMedicalEquipmentItems']);
      }
      if (name === MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING) {
        this.props.onResetFormCustom(['infoPrivateDutyNursingItems']);
      }
      if (name === MEDICAL_EXPENSE_TYPES.EXPENSES_RELATED_TO_DEATH_REPATRIATION) {
        this.props.onResetFormCustom([
          'expensesRelatedToDeathReturnRemains',
          'expensesRelatedToDeathBurialCremation',
          'infoExpensesRelatedToDeathReturnRemainsItems',
          'infoExpensesRelatedToDeathBurialCremationItems',
        ]);
      }
      if (name === MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) {
        this.props.onResetFormCustom([
          'professionalServicesPhysiotherapist',
          'professionalServicesChiropractor',
          'professionalServicesChiropodist',
          'professionalServicesOsteopath',
          'professionalServicesPodiatrist',
          'professionalServicesAcupuncturist',
          'professionalServicesOptometrist',
          'infoPhysiotherapistItems',
          'infoChiropractorItems',
          'infoChiropodistItems',
          'infoOsteopathItems',
          'infoPodiatristItems',
          'infoAcupuncturistItems',
          'infoOptometristItems',
        ]);
      }
      if (name === MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) {
        this.props.onResetFormCustom([
          'dentalServicesDentalAccident',
          'dentalServicesDentalPainOther',
          'infoDentalAccidentItems',
          'infoDentalPainOtherItems',
        ]);
      }
      if (name === MEDICAL_EXPENSE_TYPES.OTHER_UNRELATED_BENEFIT) {
        this.props.onResetFormCustom(['infoOtherUnrelatedBenefitItems']);
      }
    }
    this.props.onFormDataChange(propertyName, fromJS(listChecked));
    this.props.setFormHasChanges(true);
  };

  handleExpenseTypeChange = (formField) => (data) => {
    this.props.onFormDataChange(`form.${formField}`, fromJS(data));
    this.props.setFormHasChanges(true);
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  handleRadioButtonChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.value;

    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);

    const fieldName = propertyName.replace('form.', '');
    if (this.radioFormResets[fieldName] && this.radioFormResets[fieldName][value]) {
      this.props.onResetFormCustom(this.radioFormResets[fieldName][value]);
    }
  };

  // Handler for Hospitalization sub-checkboxes
  handleHospitalizationSubCheckbox = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);

    // Reset expense items when unchecking
    if (!value) {
      const fieldMap = {
        'form.hospitalizationHospitalFees': 'infoHospitalFeesItems',
        'form.hospitalizationInpatientPhysicianFees': 'infoInpatientPhysicianFeesItems',
        'form.hospitalizationXrayLabsDiagnostics': 'infoXrayLabsDiagnosticsItems',
        'form.hospitalizationEmergencyAirTransportation': 'infoEmergencyAirTransportationItems',
        'form.hospitalizationAirfareReturnHome': 'infoAirfareReturnHomeItems',
        'form.hospitalizationFamilyTransportation': 'infoFamilyTransportationItems',
        'form.hospitalizationFamilyTransportationMealsAccommodation': 'infoFamilyTransportationMealsAccommodationItems',
        'form.hospitalizationMedicalAttendant': 'infoMedicalAttendantItems',
        'form.hospitalizationReturnOfTravellingCompanion': 'infoReturnOfTravellingCompanionItems',
        'form.hospitalizationOutOfPocketExpenses': 'infoOutOfPocketExpensesItems',
        'form.hospitalizationReturnToUSA': 'infoReturnToUSAItems',
        'form.hospitalizationEscortOfChildren': 'infoEscortOfChildrenItems',
      };
      if (fieldMap[propertyName]) {
        this.props.onResetFormCustom([fieldMap[propertyName]]);
      }
      if (propertyName === 'form.hospitalizationReturnOfVehicle') {
        this.props.onResetFormCustom(['infoReturnOfVehicleItems', 'infoReturnOfVehicleCommercialAgencyItems', 'infoReturnOfVehicleFamilyMemberFriendItems']);
      }
    }
  };

  handleReturnOfVehicleSubCheckbox = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);

    if (!value) {
      const fieldMap = {
        'form.returnOfVehicleCommercialAgency': 'infoReturnOfVehicleCommercialAgencyItems',
        'form.returnOfVehicleFamilyMemberFriend': 'infoReturnOfVehicleFamilyMemberFriendItems',
      };
      if (fieldMap[propertyName]) {
        this.props.onResetFormCustom([fieldMap[propertyName]]);
      }
    }
  };

  // Handler for date picker fields
  handleDayChange = (propertyName) => (day) => {
    const dayWithoutTimeZone = removeTimeZoneFromDate(day);
    this.props.onFormDataChange(propertyName, dayWithoutTimeZone);
    this.props.setFormHasChanges(true);
  };

  // Handler for Maternity Benefits sub-checkboxes
  handleMaternityBenefitsSubCheckbox = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);

    // Reset expense items when unchecking
    if (!value) {
      const fieldMap = {
        'form.maternityBenefitsRoutinePreNatalCare': 'infoRoutinePreNatalCareItems',
        'form.maternityBenefitsComplicationsOfPregnancy': 'infoComplicationsOfPregnancyItems',
      };
      if (fieldMap[propertyName]) {
        this.props.onResetFormCustom([fieldMap[propertyName]]);
      }
    }
  };

  // Handler for Professional Services provider sub-checkboxes
  handleProfessionalServicesSubCheckbox = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);

    if (!value) {
      const fieldMap = {
        'form.professionalServicesPhysiotherapist': 'infoPhysiotherapistItems',
        'form.professionalServicesChiropractor': 'infoChiropractorItems',
        'form.professionalServicesChiropodist': 'infoChiropodistItems',
        'form.professionalServicesOsteopath': 'infoOsteopathItems',
        'form.professionalServicesPodiatrist': 'infoPodiatristItems',
        'form.professionalServicesAcupuncturist': 'infoAcupuncturistItems',
        'form.professionalServicesOptometrist': 'infoOptometristItems',
      };
      if (fieldMap[propertyName]) {
        this.props.onResetFormCustom([fieldMap[propertyName]]);
      }
    }
  };

  // Handler for Dental Services sub-checkboxes
  handleDentalServicesSubCheckbox = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);

    if (!value) {
      const fieldMap = {
        'form.dentalServicesDentalAccident': 'infoDentalAccidentItems',
        'form.dentalServicesDentalPainOther': 'infoDentalPainOtherItems',
      };
      if (fieldMap[propertyName]) {
        this.props.onResetFormCustom([fieldMap[propertyName]]);
      }
    }
  };

  // Handler for Expenses Related to Death sub-checkboxes
  handleExpensesRelatedToDeathSubCheckbox = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);

    if (!value) {
      const fieldMap = {
        'form.expensesRelatedToDeathReturnRemains': 'infoExpensesRelatedToDeathReturnRemainsItems',
        'form.expensesRelatedToDeathBurialCremation': 'infoExpensesRelatedToDeathBurialCremationItems',
      };
      if (fieldMap[propertyName]) {
        this.props.onResetFormCustom([fieldMap[propertyName]]);
      }
    }
  };

  appendIds = (array) =>
    array.map((item) => {
      let newItem = item;
      if (!item.id) {
        newItem.id = new Date().getTime().toString();
      }
      return newItem;
    });

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }

    const valid = this.validatorGroup.checkValidation();

    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }

    this.handleFormDataSave();
    this.props.next();
  };

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleFormDataSave = () => {
    this.props.onFormDataSave();
    this.props.setFormHasChanges(false);
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.handleFormDataSave();
    this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
    }
    if (e.key === 'Tab') {
      e.preventDefault();
    }
  };

  renderExpensesItems(formField, hideAddButton = false) {
    const items = this.props.form[formField];
    if (!items) {
      return null;
    }

    const plainItems = typeof items.toJS === 'function' ? items.toJS() : items;
    const newMedicalExpenseItems = this.appendIds(plainItems);
    const { isSummary, submitted, insuredsList, isClaimForFieldEnabled, intl } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <AdditionalList
          data={newMedicalExpenseItems}
          onChange={this.handleExpenseTypeChange(formField)}
          isDelayed={false}
          disabled={isReadOnly}
          hideAddButton={hideAddButton}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
          insuredsList={insuredsList}
          isClaimForFieldEnabled={isClaimForFieldEnabled}
          intl={intl}
        />
      </div>
    );
  }

  renderDoctorVisitCheckbox = (expensesSelected, isReadOnly) => {
    const checkboxValue = "Doctor's Visit";
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxDoctorVisit} />
        </span>
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2 mt-2">
            <b className="pt-2"><FormattedMessage {...messages.headerOutpatientPhysicianFees} /></b>
            {this.renderExpensesItems('infoOutpatientPhysicianFeesItems')}
          </div>
        )}
      </div>
    );
  };

  renderDiagnosticTestsCheckbox = (expensesSelected, isReadOnly) => {
    const checkboxValue = MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS;
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxDiagnosticTests} />
        </span>
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2 mt-2">
            <b className="pt-2"><FormattedMessage {...messages.headerDiagnosticTests} /></b>
            {this.renderExpensesItems('infoDiagnosticTestsItems')}
          </div>
        )}
      </div>
    );
  };

  renderPrescriptionDrugsCheckbox = (expensesSelected, isReadOnly) => {
    const checkboxValue = MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS;
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxPrescriptionDrugs} />
        </span>
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2 mt-2">
            <b className="pt-2"><FormattedMessage {...messages.headerPrescriptionDrugs} /></b>
            {this.renderExpensesItems('infoPrescriptionDrugsItems')}
          </div>
        )}
      </div>
    );
  };

  renderAmbulanceServicesCheckbox = (expensesSelected, isReadOnly) => {
    const checkboxValue = MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES;
    const { form } = this.props;
    const isReadOnlyForm = isReadOnly;
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnlyForm}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxAmbulanceServices} />
        </span>
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2">
            {/* Q1: Is your invoice for a ground ambulance? */}
            <div className="col-sm-12 form-group">
              <div className="mt-2">
                <PageSectionHeader>
                  <FormattedMessage {...messages.questionGroundAmbulance} />
                </PageSectionHeader>
                <FlexBox>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnlyForm}
                      type="radio"
                      value="true"
                      checked={form.ambulanceGroundService === 'true'}
                      onChange={this.handleRadioButtonChange('form.ambulanceGroundService')}
                    />
                    <FormattedMessage {...messages.yes} />
                  </OptionsContainer>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnlyForm}
                      type="radio"
                      value="false"
                      checked={form.ambulanceGroundService === 'false'}
                      onChange={this.handleRadioButtonChange('form.ambulanceGroundService')}
                    />
                    <FormattedMessage {...messages.no} />
                  </OptionsContainer>
                </FlexBox>
              </div>
              {form.ambulanceGroundService === 'true' && (
                <div className="mt-2">
                  <b className="pt-2"><FormattedMessage {...messages.headerGroundAmbulance} /></b>
                  {this.renderExpensesItems('infoGroundAmbulanceItems')}
                </div>
              )}
            </div>

            {/* Q2: Is your invoice for an Air Ambulance? - shown when ground is No */}
            {form.ambulanceGroundService === 'false' && (
              <div className="col-sm-12 form-group">
                <div className="mt-2">
                  <PageSectionHeader>
                    <FormattedMessage {...messages.questionAirAmbulance} />
                  </PageSectionHeader>
                  <FlexBox>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnlyForm}
                        type="radio"
                        value="true"
                        checked={form.ambulanceAirService === 'true'}
                        onChange={this.handleRadioButtonChange('form.ambulanceAirService')}
                      />
                      <FormattedMessage {...messages.yes} />
                    </OptionsContainer>
                    <OptionsContainer>
                      <input
                        disabled={isReadOnlyForm}
                        type="radio"
                        value="false"
                        checked={form.ambulanceAirService === 'false'}
                        onChange={this.handleRadioButtonChange('form.ambulanceAirService')}
                      />
                      <FormattedMessage {...messages.no} />
                    </OptionsContainer>
                  </FlexBox>
                </div>
                {form.ambulanceAirService === 'true' && (
                  <div className="mt-2">
                    <b className="pt-2"><FormattedMessage {...messages.headerAirAmbulance} /></b>
                    {this.renderExpensesItems('infoAirAmbulanceItems')}
                  </div>
                )}
                {form.ambulanceAirService === 'false' && (
                  <div className="mt-2">
                    <b className="pt-2"><FormattedMessage {...messages.headerOtherAmbulance} /></b>
                    {this.renderExpensesItems('infoOtherAmbulanceItems')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  renderHospitalizationCheckbox = (expensesSelected, isReadOnly, claimCodeChecks) => {
    const checkboxValue = MEDICAL_EXPENSE_TYPES.HOSPITALIZATION;
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxHospitalization} />
        </span>
        {expensesSelected.includes(checkboxValue) && (
          <span>
            <div className="col-sm-12 form-group">
              {this.renderHospitalizationQuestions(claimCodeChecks)}
            </div>
          </span>
        )}
      </div>
    );
  };

  renderHospitalizationSubCheckbox = (fieldName, headerMessage, itemsField, isReadOnly, tooltipId, tooltipMessage) => {
    const { form, intl } = this.props;
    return (
      <div className="col-sm-12 form-group mt-3">
        <input
          disabled={isReadOnly}
          type="checkbox"
          checked={form[fieldName] === true}
          onChange={this.handleHospitalizationSubCheckbox(`form.${fieldName}`)}
        />
        <span>
          <FormattedMessage {...headerMessage} />
        </span>
        {tooltipId && tooltipMessage && (
          <FNOLTooltip
            dataFor={tooltipId}
            content={intl.formatMessage(tooltipMessage)}
          />
        )}
        {form[fieldName] === true && (
          <div className="ml-4 mt-2">
            <b className="pt-2"><FormattedMessage {...headerMessage} /></b>
            {this.renderExpensesItems(itemsField)}
          </div>
        )}
      </div>
    );
  };

  renderHospitalizationQuestions = (claimCodeChecks) => {
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;
    return (
      <div className="ml-2">
        {/* Hospital Fees */}
        {claimCodeChecks.isRenderHospitalFees &&
          this.renderHospitalizationSubCheckbox('hospitalizationHospitalFees', messages.headerHospitalFees, 'infoHospitalFeesItems', isReadOnly)}

        {/* Physician Fees while hospitalized */}
        {claimCodeChecks.isRenderInpatientPhysicianFees &&
          this.renderHospitalizationSubCheckbox('hospitalizationInpatientPhysicianFees', messages.headerInpatientPhysicianFees, 'infoInpatientPhysicianFeesItems', isReadOnly)}

        {/* X-Ray, Labs, diagnostics while hospitalized */}
        {claimCodeChecks.isRenderXrayLabsDiagnostics &&
          this.renderHospitalizationSubCheckbox('hospitalizationXrayLabsDiagnostics', messages.headerXrayLabsDiagnostics, 'infoXrayLabsDiagnosticsItems', isReadOnly)}

        {/* Emergency Air Transportation */}
        {claimCodeChecks.isRenderEmergencyAirTransportation &&
          this.renderHospitalizationSubCheckbox('hospitalizationEmergencyAirTransportation', messages.headerEmergencyAirTransportation, 'infoEmergencyAirTransportationItems', isReadOnly)}

        {/* Airfare to Return Home */}
        {claimCodeChecks.isRenderAirfareReturnHome &&
          this.renderHospitalizationSubCheckbox('hospitalizationAirfareReturnHome', messages.headerAirfareReturnHome, 'infoAirfareReturnHomeItems', isReadOnly)}

        {/* Family Transportation */}
        {claimCodeChecks.isRenderFamilyTransportation &&
          this.renderHospitalizationSubCheckbox('hospitalizationFamilyTransportation', messages.headerFamilyTransportation, 'infoFamilyTransportationItems', isReadOnly)}

        {/* Family Transportation Meals and accommodation */}
        {claimCodeChecks.isRenderFamilyTransportationMealsAccommodation &&
          this.renderHospitalizationSubCheckbox('hospitalizationFamilyTransportationMealsAccommodation', messages.headerFamilyTransportationMealsAccommodation, 'infoFamilyTransportationMealsAccommodationItems', isReadOnly)}

        {/* Medical Attendant (with tooltip) */}
        {claimCodeChecks.isRenderMedicalAttendant &&
          this.renderHospitalizationSubCheckbox('hospitalizationMedicalAttendant', messages.headerMedicalAttendant, 'infoMedicalAttendantItems', isReadOnly, 'medical-attendant-tooltip', messages.tooltipMedicalAttendant)}

        {/* Return of Travelling Companion (with tooltip) */}
        {claimCodeChecks.isRenderReturnOfTravellingCompanion &&
          this.renderHospitalizationSubCheckbox('hospitalizationReturnOfTravellingCompanion', messages.headerReturnOfTravellingCompanion, 'infoReturnOfTravellingCompanionItems', isReadOnly, 'return-travelling-companion-tooltip', messages.tooltipReturnOfTravellingCompanion)}

        {/* Out-Of-Pocket Expenses (with tooltip) */}
        {claimCodeChecks.isRenderOutOfPocketExpenses &&
          this.renderHospitalizationSubCheckbox('hospitalizationOutOfPocketExpenses', messages.headerOutOfPocketExpenses, 'infoOutOfPocketExpensesItems', isReadOnly, 'out-of-pocket-expenses-tooltip', messages.tooltipOutOfPocketExpenses)}

        {/* Return to USA */}
        {claimCodeChecks.isRenderReturnToUSA &&
          this.renderHospitalizationSubCheckbox('hospitalizationReturnToUSA', messages.headerReturnToUSA, 'infoReturnToUSAItems', isReadOnly)}

        {/* Escort of Children */}
        {claimCodeChecks.isRenderEscortOfChildren &&
          this.renderHospitalizationSubCheckbox('hospitalizationEscortOfChildren', messages.headerEscortOfChildren, 'infoEscortOfChildrenItems', isReadOnly)}
      </div>
    );
  };

  renderReturnOfVehicleCheckbox = (expensesSelected, isReadOnly) => {
    const checkboxValue = MEDICAL_EXPENSE_TYPES.RETURN_OF_VEHICLE;
    const isCommercialAgencySelected = this.hasExpenseItems(this.props.form.infoReturnOfVehicleCommercialAgencyItems)
      || this.props.form.returnOfVehicleCommercialAgency === true;
    const isFamilyMemberFriendSelected = this.hasExpenseItems(this.props.form.infoReturnOfVehicleFamilyMemberFriendItems)
      || this.props.form.returnOfVehicleFamilyMemberFriend === true;
    return (
      <div className="col-sm-12 form-group mt-2">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.headerReturnOfVehicle} />
        </span>
        <FNOLTooltip
          dataFor="return-of-vehicle-tooltip"
          content={this.props.intl.formatMessage(messages.tooltipReturnOfVehicle)}
        />
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-4 mt-2">
            <div className="col-sm-12 form-group mt-2">
              <input
                disabled={isReadOnly}
                type="checkbox"
                checked={isCommercialAgencySelected}
                onChange={this.handleReturnOfVehicleSubCheckbox('form.returnOfVehicleCommercialAgency')}
              />
              <span>
                <FormattedMessage {...messages.headerReturnOfVehicleCommercialAgency} />
              </span>
              {isCommercialAgencySelected && this.renderExpensesItems('infoReturnOfVehicleCommercialAgencyItems')}
            </div>
            <div className="col-sm-12 form-group mt-2">
              <input
                disabled={isReadOnly}
                type="checkbox"
                checked={isFamilyMemberFriendSelected}
                onChange={this.handleReturnOfVehicleSubCheckbox('form.returnOfVehicleFamilyMemberFriend')}
              />
              <span>
                <FormattedMessage {...messages.headerReturnOfVehicleFamilyMemberFriend} />
              </span>
              {isFamilyMemberFriendSelected && this.renderExpensesItems('infoReturnOfVehicleFamilyMemberFriendItems')}
            </div>
            {!!this.props.form.infoReturnOfVehicleItems &&
              (((this.props.form.infoReturnOfVehicleItems.size !== undefined) && this.props.form.infoReturnOfVehicleItems.size > 0) ||
                ((this.props.form.infoReturnOfVehicleItems.length !== undefined) && this.props.form.infoReturnOfVehicleItems.length > 0)) && (
              <div className="col-sm-12 form-group mt-2">
                {this.renderExpensesItems('infoReturnOfVehicleItems')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  hasExpenseItems = (items) => {
    if (!items) return false;
    if (items.size !== undefined) return items.size > 0;
    if (items.length !== undefined) return items.length > 0;
    return false;
  };

  renderEssentialMedicalEquipmentCheckbox = (expensesSelected, isReadOnly) => {
    const { intl } = this.props;
    const checkboxValue = MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES;
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxEssentialMedicalEquipment} />
        </span>
        <FNOLTooltip
          dataFor="essential-medical-equipment-tooltip"
          content={intl.formatMessage(messages.tooltipEssentialMedicalEquipment)}
        />
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2 mt-2">
            <b className="pt-2"><FormattedMessage {...messages.headerEssentialMedicalEquipment} /></b>
            {this.renderExpensesItems('infoEssentialMedicalEquipmentItems')}
          </div>
        )}
      </div>
    );
  };

  renderPrivateDutyNursingCheckbox = (expensesSelected, isReadOnly) => {
    const { intl } = this.props;
    const checkboxValue = MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING;
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxPrivateDutyNursing} />
        </span>
        <FNOLTooltip
          dataFor="private-duty-nursing-tooltip"
          content={intl.formatMessage(messages.tooltipPrivateDutyNursing)}
        />
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2 mt-2">
            <b className="pt-2"><FormattedMessage {...messages.headerPrivateDutyNursing} /></b>
            {this.renderExpensesItems('infoPrivateDutyNursingItems')}
          </div>
        )}
      </div>
    );
  };

  renderFractureTreatmentStandaloneCheckbox = (expensesSelected, isReadOnly) => {
    const { intl } = this.props;
    const checkboxValue = MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT;
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxFractureTreatmentStandalone} />
        </span>
        <FNOLTooltip
          dataFor="fracture-treatment-standalone-tooltip"
          content={intl.formatMessage(messages.tooltipFractureTreatmentStandalone)}
        />
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2 mt-2">
            <b className="pt-2"><FormattedMessage {...messages.headerFractureTreatmentStandalone} /></b>
            {this.renderExpensesItems('infoFractureTreatmentItems')}
          </div>
        )}
      </div>
    );
  };


  renderExpensesRelatedToDeathCheckbox = (expensesSelected, isReadOnly) => {
    const { form, intl } = this.props;
    const checkboxValue = MEDICAL_EXPENSE_TYPES.EXPENSES_RELATED_TO_DEATH_REPATRIATION;
    const expensesRelatedToDeathEnabled = expensesSelected.includes(checkboxValue);

    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesRelatedToDeathEnabled}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxExpensesRelatedToDeath} />
        </span>
        <FNOLTooltip
          dataFor="expenses-related-to-death-tooltip"
          content={intl.formatMessage(messages.tooltipExpensesRelatedToDeath)}
        />
        {expensesRelatedToDeathEnabled && (
          <div className="ml-2 mt-2">
            {/* Return of remains to home country */}
            <div className="col-sm-12 form-group mt-2">
              <input
                disabled={isReadOnly}
                type="checkbox"
                checked={form.expensesRelatedToDeathReturnRemains === true}
                onChange={this.handleExpensesRelatedToDeathSubCheckbox('form.expensesRelatedToDeathReturnRemains')}
              />
              <span>
                <FormattedMessage {...messages.headerExpensesRelatedToDeathReturnOfRemains} />
              </span>
              {form.expensesRelatedToDeathReturnRemains === true && (
                <div className="ml-4 mt-2">
                  <b className="pt-2">
                    <FormattedMessage {...messages.headerExpensesRelatedToDeathReturnOfRemains} />
                  </b>
                  {this.renderExpensesItems('infoExpensesRelatedToDeathReturnRemainsItems')}
                </div>
              )}
            </div>

            {/* Burial or Cremation at Place of Death */}
            <div className="col-sm-12 form-group mt-2">
              <input
                disabled={isReadOnly}
                type="checkbox"
                checked={form.expensesRelatedToDeathBurialCremation === true}
                onChange={this.handleExpensesRelatedToDeathSubCheckbox('form.expensesRelatedToDeathBurialCremation')}
              />
              <span>
                <FormattedMessage {...messages.headerExpensesRelatedToDeathBurialOrCremationAtPlaceOfDeath} />
              </span>
              {form.expensesRelatedToDeathBurialCremation === true && (
                <div className="ml-4 mt-2">
                  <b className="pt-2">
                    <FormattedMessage {...messages.headerExpensesRelatedToDeathBurialOrCremationAtPlaceOfDeath} />
                  </b>
                  {this.renderExpensesItems('infoExpensesRelatedToDeathBurialCremationItems')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  renderMaternityBenefitsCheckbox = (expensesSelected, isReadOnly) => {
    const { intl } = this.props;
    const checkboxValue = MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS;
    const { form, isSummary, submitted } = this.props;
    const isReadOnlyField = submitted || isSummary;

    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxMaternityBenefits} />
        </span>
        {expensesSelected.includes(checkboxValue) && (
          <div className="col-sm-12 form-group ml-4 mt-2">
            <div className="row">
              {/* Date of Conception */}
              <div className="form-group col-sm-6">
                <Validate
                  name="maternityBenefitsDateOfConception"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={form.maternityBenefitsDateOfConception}
                  fieldName={messages.dateOfConception}
                  tooltipContent={intl.formatMessage(messages.dateOfConceptionTooltip)}
                >
                  <DatePicker
                    position={'bottom-start'}
                    disabled={isReadOnlyField}
                    name="maternityBenefitsDateOfConception"
                    type="text"
                    onDayChange={this.handleDayChange('form.maternityBenefitsDateOfConception')}
                    value={form.maternityBenefitsDateOfConception}
                    placeholder=""
                    allowFutureDates={true}
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage
                      {...messages.isRequired}
                      values={messages.dateOfConception}
                    />
                  </ErrorMessage>
                </Validate>
              </div>
              {/* Anticipated Due Date */}
              <div className="form-group col-sm-6 mt-2">
                <label className="mb-0">
                  <FormattedMessage {...messages.anticipatedDueDate} />
                </label>
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnlyField}
                  name="maternityBenefitsAnticipatedDueDate"
                  type="text"
                  onDayChange={this.handleDayChange('form.maternityBenefitsAnticipatedDueDate')}
                  value={form.maternityBenefitsAnticipatedDueDate}
                  placeholder=""
                  allowFutureDates={true}
                />
              </div>
            </div>

            {/* Routine Pre-Natal Care Checkbox */}
            <div className="col-sm-12 form-group mt-3">
              <input
                disabled={isReadOnly}
                type="checkbox"
                checked={form.maternityBenefitsRoutinePreNatalCare === true}
                onChange={this.handleMaternityBenefitsSubCheckbox('form.maternityBenefitsRoutinePreNatalCare')}
              />
              <span>
                <FormattedMessage {...messages.headerRoutinePreNatalCare} />
              </span>
              {form.maternityBenefitsRoutinePreNatalCare === true && (
                <div className="ml-4 mt-2">
                  {this.renderExpensesItems('infoRoutinePreNatalCareItems')}
                </div>
              )}
            </div>

            {/* Complications of Pregnancy Checkbox */}
            <div className="col-sm-12 form-group mt-3">
              <input
                disabled={isReadOnly}
                type="checkbox"
                checked={form.maternityBenefitsComplicationsOfPregnancy === true}
                onChange={this.handleMaternityBenefitsSubCheckbox('form.maternityBenefitsComplicationsOfPregnancy')}
              />
              <span>
                <FormattedMessage {...messages.headerComplicationsOfPregnancy} />
              </span>
              {form.maternityBenefitsComplicationsOfPregnancy === true && (
                <div className="ml-4 mt-2">
                  {this.renderExpensesItems('infoComplicationsOfPregnancyItems')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  renderProfessionalServicesSubCheckbox = (fieldName, headerMessage, itemsField, isReadOnly) => {
    const { form } = this.props;
    return (
      <div className="col-sm-12 form-group mt-3">
        <input
          disabled={isReadOnly}
          type="checkbox"
          checked={form[fieldName] === true}
          onChange={this.handleProfessionalServicesSubCheckbox(`form.${fieldName}`)}
        />
        <span>
          <FormattedMessage {...headerMessage} />
        </span>
        {form[fieldName] === true && (
          <div className="ml-4 mt-2">
            <b className="pt-2"><FormattedMessage {...headerMessage} /></b>
            {this.renderExpensesItems(itemsField)}
          </div>
        )}
      </div>
    );
  };

  renderDentalServicesSubCheckbox = (fieldName, headerMessage, itemsField, isReadOnly) => {
    const { form } = this.props;
    return (
      <div className="col-sm-12 form-group mt-3">
        <input
          disabled={isReadOnly}
          type="checkbox"
          checked={form[fieldName] === true}
          onChange={this.handleDentalServicesSubCheckbox(`form.${fieldName}`)}
        />
        <span>
          <FormattedMessage {...headerMessage} />
        </span>
        {form[fieldName] === true && (
          <div className="ml-4 mt-2">
            {this.renderExpensesItems(itemsField)}
          </div>
        )}
      </div>
    );
  };

  renderProfessionalServicesCheckbox = (expensesSelected, isReadOnly, claimCodeChecks) => {
    const checkboxValue = MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES;
    const { form } = this.props;
    const professionalServicesEnabled = expensesSelected.includes(checkboxValue);
    const providerKeys = Object.values(PROFESSIONAL_SERVICES_PROVIDERS);
    const selectedProviders = professionalServicesEnabled
      ? providerKeys.filter((key) => form[`professionalServices${key.charAt(0).toUpperCase()}${key.slice(1)}`] === true)
      : [];
    const validationValue = professionalServicesEnabled ? selectedProviders : ['_skip'];

    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxProfessionalServices} />
        </span>
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2 mt-2">
            <p className="text-muted">
              <FormattedMessage {...messages.professionalServicesDescription} />
            </p>
            <Validate
              name="professionalServicesProviderRequired"
              validators={[required]}
              group={this.validatorGroup}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              defaultValue={validationValue}
              fieldName={messages.checkboxProfessionalServices}
            >
              <div className="ml-2">
                {claimCodeChecks.isRenderPhysiotherapist &&
                  this.renderProfessionalServicesSubCheckbox('professionalServicesPhysiotherapist', messages.professionalServicesPhysiotherapist, 'infoPhysiotherapistItems', isReadOnly)}
                {claimCodeChecks.isRenderChiropractor &&
                  this.renderProfessionalServicesSubCheckbox('professionalServicesChiropractor', messages.professionalServicesChiropractor, 'infoChiropractorItems', isReadOnly)}
                {claimCodeChecks.isRenderChiropodist &&
                  this.renderProfessionalServicesSubCheckbox('professionalServicesChiropodist', messages.professionalServicesChiropodist, 'infoChiropodistItems', isReadOnly)}
                {claimCodeChecks.isRenderOsteopath &&
                  this.renderProfessionalServicesSubCheckbox('professionalServicesOsteopath', messages.professionalServicesOsteopath, 'infoOsteopathItems', isReadOnly)}
                {claimCodeChecks.isRenderPodiatrist &&
                  this.renderProfessionalServicesSubCheckbox('professionalServicesPodiatrist', messages.professionalServicesPodiatrist, 'infoPodiatristItems', isReadOnly)}
              </div>
              <ErrorMessage for="required">
                <FormattedMessage {...messages.professionalServicesSelectOneRequired} />
              </ErrorMessage>
            </Validate>
          </div>
        )}
      </div>
    );
  };

  renderDentalServicesCheckbox = (expensesSelected, isReadOnly) => {
    const checkboxValue = MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES;
    const { form } = this.props;
    const dentalServicesEnabled = expensesSelected.includes(checkboxValue);

    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={dentalServicesEnabled}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxDentalServices} />
        </span>
        {dentalServicesEnabled && (
          <div className="ml-2 mt-2">
            <div className="col-sm-12 form-group mt-2">
              {this.renderDentalServicesSubCheckbox(
                'dentalServicesDentalAccident',
                messages.dentalServicesDentalAccident,
                'infoDentalAccidentItems',
                isReadOnly
              )}
              {this.renderDentalServicesSubCheckbox(
                'dentalServicesDentalPainOther',
                messages.dentalServicesDentalPainOther,
                'infoDentalPainOtherItems',
                isReadOnly
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  renderOtherUnrelatedBenefitCheckbox = (expensesSelected, isReadOnly) => {
    const checkboxValue = MEDICAL_EXPENSE_TYPES.OTHER_UNRELATED_BENEFIT;
    return (
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={checkboxValue}
          checked={expensesSelected.includes(checkboxValue)}
          onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
        />
        <span>
          <FormattedMessage {...messages.checkboxOtherUnrelatedBenefit} />
        </span>
        {expensesSelected.includes(checkboxValue) && (
          <div className="ml-2 mt-2">
            <b className="pt-2"><FormattedMessage {...messages.headerOtherUnrelatedBenefit} /></b>
            {this.renderExpensesItems('infoOtherUnrelatedBenefitItems')}
          </div>
        )}
      </div>
    );
  };

  renderAllMedicalExpenseCheckboxes = (expensesSelected, isReadOnly, claimCodeChecks) => {
    const checkboxes = [
      {
        render: this.renderDoctorVisitCheckbox,
        key: 'doctorVisit',
        shouldRender: claimCodeChecks.isRenderDoctorVisit,
      },
      {
        render: this.renderDiagnosticTestsCheckbox,
        key: 'diagnosticTests',
        shouldRender: claimCodeChecks.isRenderDiagnosticTests,
      },
      {
        render: this.renderPrescriptionDrugsCheckbox,
        key: 'prescriptionDrugs',
        shouldRender: claimCodeChecks.isRenderPrescriptionDrugs,
      },
      {
        render: this.renderAmbulanceServicesCheckbox,
        key: 'ambulanceServices',
        shouldRender: claimCodeChecks.isRenderAmbulanceServices,
      },
      {
        render: this.renderHospitalizationCheckbox,
        key: 'hospitalization',
        shouldRender: claimCodeChecks.isRenderHospitalization,
      },
      {
        render: this.renderEssentialMedicalEquipmentCheckbox,
        key: 'essentialMedicalEquipment',
        shouldRender: claimCodeChecks.isRenderEssentialMedicalEquipment,
      },
      {
        render: this.renderReturnOfVehicleCheckbox,
        key: 'returnOfVehicle',
        shouldRender: claimCodeChecks.isRenderReturnOfVehicle,
      },
      {
        render: this.renderPrivateDutyNursingCheckbox,
        key: 'privateDutyNursing',
        shouldRender: claimCodeChecks.isRenderPrivateDutyNursing,
      },
      {
        render: this.renderProfessionalServicesCheckbox,
        key: 'professionalServices',
        shouldRender: claimCodeChecks.isRenderProfessionalServices,
      },
      {
        render: this.renderDentalServicesCheckbox,
        key: 'dentalServices',
        shouldRender: claimCodeChecks.isRenderDentalServices,
      },
      {
        render: this.renderExpensesRelatedToDeathCheckbox,
        key: 'expensesRelatedToDeath',
        shouldRender: claimCodeChecks.isRenderExpensesRelatedToDeath,
      },
      {
        render: this.renderOtherUnrelatedBenefitCheckbox,
        key: 'otherUnrelatedBenefit',
        shouldRender: claimCodeChecks.isRenderOtherUnrelatedBenefit,
      },
    ];

    return (
      <div>
       
        {checkboxes.map(({ render, key, shouldRender }) => (
          shouldRender && (
            <div key={key}>
              {render(expensesSelected, isReadOnly, claimCodeChecks)}
            </div>
          )
        ))}
      </div>
    );
  };

  render() {
    const {
      whatExpensesAreYouClaimingFor,
    } = this.props.isSummary ? this.summaryFormValue() : this.props.form;
    const { isSummary, submitted, loading, intl } = this.props;
    const isReadOnly = submitted || isSummary;
    const expensesSelected = !!whatExpensesAreYouClaimingFor && typeof whatExpensesAreYouClaimingFor.toJS === 'function'
      ? whatExpensesAreYouClaimingFor.toJS()
      : whatExpensesAreYouClaimingFor || [];

    const claimCodeChecks = {
      isRenderDoctorVisit: !!(stepOptimumMedicalBenefitsTugoLookup.outpatientPhysicianFees &&
        (stepOptimumMedicalBenefitsTugoLookup.outpatientPhysicianFees.claimCode || (stepOptimumMedicalBenefitsTugoLookup.outpatientPhysicianFees.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.outpatientPhysicianFees.associatedClaimCodes.length))),
      isRenderDiagnosticTests: !!(stepOptimumMedicalBenefitsTugoLookup.diagnosticTests &&
        (stepOptimumMedicalBenefitsTugoLookup.diagnosticTests.claimCode || (stepOptimumMedicalBenefitsTugoLookup.diagnosticTests.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.diagnosticTests.associatedClaimCodes.length))),
      isRenderPrescriptionDrugs: !!(stepOptimumMedicalBenefitsTugoLookup.prescriptionDrugs &&
        (stepOptimumMedicalBenefitsTugoLookup.prescriptionDrugs.claimCode || (stepOptimumMedicalBenefitsTugoLookup.prescriptionDrugs.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.prescriptionDrugs.associatedClaimCodes.length))),
      isRenderAmbulanceServices: !!(stepOptimumMedicalBenefitsTugoLookup.groundAmbulance &&
        (stepOptimumMedicalBenefitsTugoLookup.groundAmbulance.claimCode || (stepOptimumMedicalBenefitsTugoLookup.groundAmbulance.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.groundAmbulance.associatedClaimCodes.length))),
      // Hospitalization
      isRenderHospitalization: !!(stepOptimumMedicalBenefitsTugoLookup.hospitalFees &&
        (stepOptimumMedicalBenefitsTugoLookup.hospitalFees.claimCode || (stepOptimumMedicalBenefitsTugoLookup.hospitalFees.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.hospitalFees.associatedClaimCodes.length))),
      isRenderHospitalFees: !!(stepOptimumMedicalBenefitsTugoLookup.hospitalFees &&
        (stepOptimumMedicalBenefitsTugoLookup.hospitalFees.claimCode || (stepOptimumMedicalBenefitsTugoLookup.hospitalFees.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.hospitalFees.associatedClaimCodes.length))),
      isRenderInpatientPhysicianFees: !!(stepOptimumMedicalBenefitsTugoLookup.inpatientPhysicianFees &&
        (stepOptimumMedicalBenefitsTugoLookup.inpatientPhysicianFees.claimCode || (stepOptimumMedicalBenefitsTugoLookup.inpatientPhysicianFees.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.inpatientPhysicianFees.associatedClaimCodes.length))),
      isRenderXrayLabsDiagnostics: !!(stepOptimumMedicalBenefitsTugoLookup.xrayLabsDiagnostics &&
        (stepOptimumMedicalBenefitsTugoLookup.xrayLabsDiagnostics.claimCode || (stepOptimumMedicalBenefitsTugoLookup.xrayLabsDiagnostics.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.xrayLabsDiagnostics.associatedClaimCodes.length))),
      isRenderEmergencyAirTransportation: !!(stepOptimumMedicalBenefitsTugoLookup.emergencyAirTransportation &&
        (stepOptimumMedicalBenefitsTugoLookup.emergencyAirTransportation.claimCode || (stepOptimumMedicalBenefitsTugoLookup.emergencyAirTransportation.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.emergencyAirTransportation.associatedClaimCodes.length))),
      isRenderAirfareReturnHome: !!(stepOptimumMedicalBenefitsTugoLookup.airfareReturnHome &&
        (stepOptimumMedicalBenefitsTugoLookup.airfareReturnHome.claimCode || (stepOptimumMedicalBenefitsTugoLookup.airfareReturnHome.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.airfareReturnHome.associatedClaimCodes.length))),
      isRenderFamilyTransportation: !!(stepOptimumMedicalBenefitsTugoLookup.familyTransportation &&
        (stepOptimumMedicalBenefitsTugoLookup.familyTransportation.claimCode || (stepOptimumMedicalBenefitsTugoLookup.familyTransportation.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.familyTransportation.associatedClaimCodes.length))),
      isRenderFamilyTransportationMealsAccommodation: !!(stepOptimumMedicalBenefitsTugoLookup.familyTransportationMealsAccommodation &&
        (stepOptimumMedicalBenefitsTugoLookup.familyTransportationMealsAccommodation.claimCode || (stepOptimumMedicalBenefitsTugoLookup.familyTransportationMealsAccommodation.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.familyTransportationMealsAccommodation.associatedClaimCodes.length))),
      isRenderMedicalAttendant: !!(stepOptimumMedicalBenefitsTugoLookup.medicalAttendant &&
        (stepOptimumMedicalBenefitsTugoLookup.medicalAttendant.claimCode || (stepOptimumMedicalBenefitsTugoLookup.medicalAttendant.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.medicalAttendant.associatedClaimCodes.length))),
      isRenderReturnOfTravellingCompanion: !!(stepOptimumMedicalBenefitsTugoLookup.returnOfTravellingCompanion &&
        (stepOptimumMedicalBenefitsTugoLookup.returnOfTravellingCompanion.claimCode || (stepOptimumMedicalBenefitsTugoLookup.returnOfTravellingCompanion.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.returnOfTravellingCompanion.associatedClaimCodes.length))),
      isRenderOutOfPocketExpenses: !!(stepOptimumMedicalBenefitsTugoLookup.outOfPocketExpenses &&
        (stepOptimumMedicalBenefitsTugoLookup.outOfPocketExpenses.claimCode || (stepOptimumMedicalBenefitsTugoLookup.outOfPocketExpenses.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.outOfPocketExpenses.associatedClaimCodes.length))),
      isRenderReturnToUSA: !!(stepOptimumMedicalBenefitsTugoLookup.returnToUSA &&
        (stepOptimumMedicalBenefitsTugoLookup.returnToUSA.claimCode || (stepOptimumMedicalBenefitsTugoLookup.returnToUSA.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.returnToUSA.associatedClaimCodes.length))),
      isRenderEscortOfChildren: !!(stepOptimumMedicalBenefitsTugoLookup.escortOfChildren &&
        (stepOptimumMedicalBenefitsTugoLookup.escortOfChildren.claimCode || (stepOptimumMedicalBenefitsTugoLookup.escortOfChildren.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.escortOfChildren.associatedClaimCodes.length))),
      isRenderReturnOfVehicle: !!(stepOptimumMedicalBenefitsTugoLookup.returnOfVehicleCommercialAgency &&
        (stepOptimumMedicalBenefitsTugoLookup.returnOfVehicleCommercialAgency.claimCode || (stepOptimumMedicalBenefitsTugoLookup.returnOfVehicleCommercialAgency.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.returnOfVehicleCommercialAgency.associatedClaimCodes.length))) ||
        !!(stepOptimumMedicalBenefitsTugoLookup.returnOfVehicleFamilyMemberFriend &&
          (stepOptimumMedicalBenefitsTugoLookup.returnOfVehicleFamilyMemberFriend.claimCode || (stepOptimumMedicalBenefitsTugoLookup.returnOfVehicleFamilyMemberFriend.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.returnOfVehicleFamilyMemberFriend.associatedClaimCodes.length))) ||
        !!(stepOptimumMedicalBenefitsTugoLookup.returnOfVehicle &&
          (stepOptimumMedicalBenefitsTugoLookup.returnOfVehicle.claimCode || (stepOptimumMedicalBenefitsTugoLookup.returnOfVehicle.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.returnOfVehicle.associatedClaimCodes.length))),
      // Essential Medical Equipment, Private Duty Nursing
      isRenderEssentialMedicalEquipment: !!(stepOptimumMedicalBenefitsTugoLookup.essentialMedicalEquipment && stepOptimumMedicalBenefitsTugoLookup.essentialMedicalEquipment.claimCode),
      isRenderPrivateDutyNursing: !!(stepOptimumMedicalBenefitsTugoLookup.privateDutyNursing && stepOptimumMedicalBenefitsTugoLookup.privateDutyNursing.claimCode),

      // Expenses Related to Death
      isRenderExpensesRelatedToDeath:
        !!(stepOptimumMedicalBenefitsTugoLookup.deathReturnOfRemains &&
          (stepOptimumMedicalBenefitsTugoLookup.deathReturnOfRemains.claimCode ||
            (stepOptimumMedicalBenefitsTugoLookup.deathReturnOfRemains.associatedClaimCodes &&
              stepOptimumMedicalBenefitsTugoLookup.deathReturnOfRemains.associatedClaimCodes.length))) ||
        !!(stepOptimumMedicalBenefitsTugoLookup.deathBurialCremation &&
          (stepOptimumMedicalBenefitsTugoLookup.deathBurialCremation.claimCode ||
            (stepOptimumMedicalBenefitsTugoLookup.deathBurialCremation.associatedClaimCodes &&
              stepOptimumMedicalBenefitsTugoLookup.deathBurialCremation.associatedClaimCodes.length))),
      // Professional Services
      isRenderProfessionalServices: !!(stepOptimumMedicalBenefitsTugoLookup.physiotherapist &&
        (stepOptimumMedicalBenefitsTugoLookup.physiotherapist.claimCode ||
          (stepOptimumMedicalBenefitsTugoLookup.physiotherapist.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.physiotherapist.associatedClaimCodes.length))),
      isRenderPhysiotherapist: !!(stepOptimumMedicalBenefitsTugoLookup.physiotherapist &&
        (stepOptimumMedicalBenefitsTugoLookup.physiotherapist.claimCode || (stepOptimumMedicalBenefitsTugoLookup.physiotherapist.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.physiotherapist.associatedClaimCodes.length))),
      isRenderChiropractor: !!(stepOptimumMedicalBenefitsTugoLookup.chiropractor &&
        (stepOptimumMedicalBenefitsTugoLookup.chiropractor.claimCode || (stepOptimumMedicalBenefitsTugoLookup.chiropractor.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.chiropractor.associatedClaimCodes.length))),
      isRenderChiropodist: !!(stepOptimumMedicalBenefitsTugoLookup.chiropodist &&
        (stepOptimumMedicalBenefitsTugoLookup.chiropodist.claimCode || (stepOptimumMedicalBenefitsTugoLookup.chiropodist.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.chiropodist.associatedClaimCodes.length))),
      isRenderOsteopath: !!(stepOptimumMedicalBenefitsTugoLookup.osteopath &&
        (stepOptimumMedicalBenefitsTugoLookup.osteopath.claimCode || (stepOptimumMedicalBenefitsTugoLookup.osteopath.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.osteopath.associatedClaimCodes.length))),
      isRenderPodiatrist: !!(stepOptimumMedicalBenefitsTugoLookup.podiatrist &&
        (stepOptimumMedicalBenefitsTugoLookup.podiatrist.claimCode || (stepOptimumMedicalBenefitsTugoLookup.podiatrist.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.podiatrist.associatedClaimCodes.length))),
      // Dental Services
      isRenderDentalServices: !!(stepOptimumMedicalBenefitsTugoLookup.dentalAccident &&
        (stepOptimumMedicalBenefitsTugoLookup.dentalAccident.claimCode ||
          (stepOptimumMedicalBenefitsTugoLookup.dentalAccident.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.dentalAccident.associatedClaimCodes.length))) ||
        !!(stepOptimumMedicalBenefitsTugoLookup.dentalPainOther &&
          (stepOptimumMedicalBenefitsTugoLookup.dentalPainOther.claimCode ||
            (stepOptimumMedicalBenefitsTugoLookup.dentalPainOther.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.dentalPainOther.associatedClaimCodes.length))),
      isRenderOtherUnrelatedBenefit: !!(stepOptimumMedicalBenefitsTugoLookup.otherUnrelatedBenefit &&
        (stepOptimumMedicalBenefitsTugoLookup.otherUnrelatedBenefit.claimCode || (stepOptimumMedicalBenefitsTugoLookup.otherUnrelatedBenefit.associatedClaimCodes && stepOptimumMedicalBenefitsTugoLookup.otherUnrelatedBenefit.associatedClaimCodes.length))),
    };
    return (
      <div className="app-content">
        <div>
          <div className="col-sm-12 form-group">
            <PageSectionHeader>
              <FormattedMessage {...messages.thisSectionRelatedToMedicalExpenses} />
            </PageSectionHeader>
          </div>
          <div className="col-sm-12 form-group">
            <Validate
              name="whatExpensesAreYouClaimingFor"
              validators={[required]}
              group={this.validatorGroup}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              defaultValue={expensesSelected}
              fieldName={messages.whatExpensesAreYouClaimingFor}
            >
              {this.renderAllMedicalExpenseCheckboxes(expensesSelected, isReadOnly, claimCodeChecks)}
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.whatExpensesAreYouClaimingFor}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        {!isSummary && (
          <WizardNavigationButtons
            onBack={this.props.prev}
            onNext={this.handleNext}
            onSave={this.handleSaveButtonClick}
            canMoveNext
            listLength={1}
            submitted={submitted}
            loading={loading}
            saveButtonId={this.saveButtonId}
            showSpinnerForElement={this.showSpinnerForElement}
            handleKeyDown={this.handleKeyDown}
            intl={intl}
            hasFormChanges={this.props.hasFormChanges}
            nextButtonRef={(ref) => {
              this.nextButton = ref;
            }}
          />
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

OptimumMedicalBenefitsTugo.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  loading: PropTypes.bool,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  adjust: PropTypes.func,
  makeValid: PropTypes.func,
  makeError: PropTypes.func,
  onNext: PropTypes.func,
  step: PropTypes.number,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnly: PropTypes.bool,
  errors: PropTypes.any,
  onResetFormCustom: PropTypes.func,
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
  intl: intlShape.isRequired,
  insuredsList: PropTypes.any,
  isClaimForFieldEnabled: PropTypes.bool,
  hasFormChanges: PropTypes.bool,
  setFormHasChanges: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectOptimumMedicalBenefitsTugo(),
  errors: makeSelectOptimumMedicalBenefitsTugoErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
  insuredsList: makeSelectInsuredsList(),
  isClaimForFieldEnabled: makeSelectIsClaimForFieldEnabled(),
  hasFormChanges: makeSelectFormHasChanges(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) => dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onResetFormCustom: (fields) => dispatch(resetFormFields(fields)),
  setFormHasChanges: (hasFormChanges) => dispatch(setFormHasChanges(hasFormChanges)),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps
);
const withReducer = injectReducer({ key: 'optimumMedicalBenefitsTugo', reducer });

export default compose(
  withReducer,
  withConnect,
  injectIntl,
)(OptimumMedicalBenefitsTugo);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.OptimumMedicalBenefits/index.js